import { createContext, useContext, useState, useEffect } from 'react';
import GitHubClient from '../api/GitHubClient';
import GitLabClient from '../api/GitLabClient';
import FileLoaderService from '../api/FileLoaderService';
import TwinCATParserService from '../api/TwinCATParserService';
import { isTwinCATFile } from '../constants/fileExtensions';
import { processDroppedItems, processUploadedFiles } from '../utils/fileSystem';
import { trackLocalFilesUploaded, trackError } from '../utils/analyticsEvents';

const FileContext = createContext();

export const FileProvider = ({ children }) => {
  // GitHub configuration
  const [githubLink, setGithubLink] = useState('');
  const [githubNameAndRepo, setGithubNameAndRepo] = useState('');
  const [githubAccessToken, setGithubAccessToken] = useState('');
  const [githubBranch, setGithubBranch] = useState('');

  // GitLab configuration
  const [gitlabLink, setGitlabLink] = useState('');
  const [gitlabNameAndRepo, setGitlabNameAndRepo] = useState('');
  const [gitlabAccessToken, setGitlabAccessToken] = useState('');
  const [gitlabBranch, setGitlabBranch] = useState('');
  const [gitlabBaseUrl, setGitlabBaseUrl] = useState('https://gitlab.com');

  // File management
  const [localFiles, setLocalFiles] = useState([]);
  const [source, setSource] = useState('github');

  // Content display
  const [filePath, setFilePath] = useState('');
  const [fileName, setFileName] = useState('');
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focusedFilePath, setFocusedFilePath] = useState(null);
  const [selectedFilePath, setSelectedFilePath] = useState(null);

  // Read URL on mount and detect provider
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlParam = params.get('url');
    if (urlParam) {
      if (urlParam.includes('gitlab.com')) {
        setGitlabLink(urlParam);
        setSource('gitlab');
      } else {
        setGithubLink(urlParam);
        setSource('github');
      }
    }
  }, []);

  // Clear state when githubLink is cleared
  useEffect(() => {
    if (githubLink === '') {
      setGithubNameAndRepo('');
      setGithubBranch('');
      if (source === 'github') {
        setFocusedFilePath(null);
        setSelectedFilePath(null);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [githubLink]);

  // Clear state when gitlabLink is cleared
  useEffect(() => {
    if (gitlabLink === '') {
      setGitlabNameAndRepo('');
      setGitlabBranch('');
      if (source === 'gitlab') {
        setFocusedFilePath(null);
        setSelectedFilePath(null);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gitlabLink]);

  // Sync state to URL
  useEffect(() => {
    const currentUrl = new URL(window.location.href);

    if (source === 'github' && githubNameAndRepo && githubLink) {
      let newUrl = `https://github.com${githubNameAndRepo}`;
      if (githubBranch) {
        const path = selectedFilePath || focusedFilePath;
        if (path) {
          const isFile = path.includes('.');
          newUrl += `/${isFile ? 'blob' : 'tree'}/${githubBranch}/${path}`;
        } else {
          newUrl += `/tree/${githubBranch}`;
        }
      }
      if (currentUrl.searchParams.get('url') !== newUrl) {
        currentUrl.searchParams.set('url', newUrl);
        window.history.replaceState({}, '', currentUrl.toString());
      }
    } else if (source === 'gitlab' && gitlabNameAndRepo && gitlabLink) {
      let newUrl = `${gitlabBaseUrl}${gitlabNameAndRepo}`;
      if (gitlabBranch) {
        const path = selectedFilePath || focusedFilePath;
        if (path) {
          const isFile = path.includes('.');
          newUrl += `/-/${isFile ? 'blob' : 'tree'}/${gitlabBranch}/${path}`;
        } else {
          newUrl += `/-/tree/${gitlabBranch}`;
        }
      }
      if (currentUrl.searchParams.get('url') !== newUrl) {
        currentUrl.searchParams.set('url', newUrl);
        window.history.replaceState({}, '', currentUrl.toString());
      }
    } else if (source === 'local') {
      if (currentUrl.searchParams.has('url')) {
        currentUrl.searchParams.delete('url');
        window.history.replaceState({}, '', currentUrl.pathname);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, githubNameAndRepo, githubBranch, gitlabNameAndRepo, gitlabBranch, gitlabBaseUrl, selectedFilePath, focusedFilePath]);

  const parserService = new TwinCATParserService();

  const handleFilesAndFolders = async (items) => {
    const droppedFiles = await processDroppedItems(items);
    setLocalFiles(prev => [...prev, ...droppedFiles]);
    setSource('local');
    trackLocalFilesUploaded();
  };

  const handleLocalUpload = (filesList) => {
    const filesArray = processUploadedFiles(filesList);
    setLocalFiles(filesArray);
    setSource('local');
    trackLocalFilesUploaded();
  };

  const clearLocalFiles = () => {
    setLocalFiles([]);
  };

  const loadFile = async (node) => {
    setIsLoading(true);
    try {
      setFileName(node.name);
      setFilePath(node.path);

      let decoded = '';
      if (source === 'local') {
        const loaderService = new FileLoaderService(null);
        decoded = await loaderService.loadFromLocal(node.fileRef);
      } else if (source === 'gitlab') {
        const gitlabClient = new GitLabClient(gitlabAccessToken, gitlabBaseUrl);
        const loaderService = new FileLoaderService(null, gitlabClient);
        decoded = await loaderService.loadFromGitlab(gitlabNameAndRepo, node.path, gitlabBranch);
      } else {
        const githubClient = new GitHubClient(githubAccessToken);
        const loaderService = new FileLoaderService(githubClient);
        decoded = await loaderService.loadFromGithub(githubNameAndRepo, node.path, githubBranch);
      }

      let parsedContent = {};
      if (isTwinCATFile(node.path)) {
        parsedContent = parserService.parseTwinCAT(decoded);
      } else {
        parsedContent = parserService.parseGeneric(decoded);
      }

      setContent(parsedContent);
    } catch (error) {
      console.error('Error loading file:', error);
      setContent({ xmlString: `Error loading file: ${error.message}` });
      trackError('File Load Error');
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    // GitHub configuration
    githubLink,
    setGithubLink,
    githubNameAndRepo,
    setGithubNameAndRepo,
    githubAccessToken,
    setGithubAccessToken,
    githubBranch,
    setGithubBranch,
    // GitLab configuration
    gitlabLink,
    setGitlabLink,
    gitlabNameAndRepo,
    setGitlabNameAndRepo,
    gitlabAccessToken,
    setGitlabAccessToken,
    gitlabBranch,
    setGitlabBranch,
    gitlabBaseUrl,
    setGitlabBaseUrl,
    // File management
    localFiles,
    setLocalFiles,
    source,
    setSource,
    handleFilesAndFolders,
    handleLocalUpload,
    clearLocalFiles,
    // Content display
    filePath,
    setFilePath,
    fileName,
    setFileName,
    content,
    isLoading,
    focusedFilePath,
    setFocusedFilePath,
    selectedFilePath,
    setSelectedFilePath,
    // Actions
    loadFile
  };

  return <FileContext.Provider value={value}>{children}</FileContext.Provider>;
};

export const useFile = () => {
  const context = useContext(FileContext);
  if (!context) {
    throw new Error('useFile must be used within a FileProvider');
  }
  return context;
};
